import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiFileText,
  FiPaperclip,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import { taskApi } from "api/tasks/taskApi";
import { useAlert } from "components/UI_components/Alert";

const STATUS_CARD_CONFIG = [
  {
    key: "overdue",
    title: "Overdue",
    cardClass: "bg-[#FCF4F4] border border-[#FBD4D3]",
    dotClass: "bg-[#EF4443]",
    countClass: "text-[#EF4443] border-red-200",
  },
  {
    key: "ongoing",
    title: "Ongoing",
    cardClass: "bg-[#FDF7F1] border border-[#FEDFC9]",
    dotClass: "bg-orange-500",
    countClass: "text-orange-500 border-orange-200",
  },
  {
    key: "notStarted",
    title: "Not Started",
    cardClass: "bg-[#FAFAFA] border border-gray-200",
    dotClass: "bg-gray-500",
    countClass: "text-gray-500 border-gray-200",
  },
  {
    key: "halted",
    title: "Halted",
    cardClass: "bg-[#D6D0F5] border border-[#F9F9FA]",
    dotClass: "bg-[#3B0069]",
    countClass: "text-gray-500 border-gray-200",
  },
];

const getInitials = (name = "User") =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "U";

const getDisplayName = (rawValue = "User") => {
  const value = String(rawValue || "").trim();
  if (!value) return "User";

  const parts = value
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
  const candidate = parts.length > 1 ? parts[parts.length - 1] : value;

  if (candidate.includes("@")) {
    const emailPrefix = candidate.split("@")[0] || "";
    const fallbackName = emailPrefix.replace(/[._-]+/g, " ").trim();
    return fallbackName || "User";
  }

  return candidate;
};

const formatDateShort = (dateValue) => {
  if (!dateValue) return "--";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "--";
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const parseDateOnly = (dateValue) => {
  if (!dateValue) return null;

  if (
    typeof dateValue === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(dateValue.trim())
  ) {
    const [year, month, day] = dateValue.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const getNormalizedDateRange = (startValue, endValue) => {
  const start = parseDateOnly(startValue);
  const end = parseDateOnly(endValue);

  if (start && end && start > end) {
    return { start: end, end: start };
  }

  return { start, end };
};

const taskMatchesDateRange = (task, startValue, endValue) => {
  const { start, end } = getNormalizedDateRange(startValue, endValue);
  if (!start && !end) return true;

  const taskStart = parseDateOnly(task?.startDate || task?.start_date);
  const taskEnd = parseDateOnly(task?.endDate || task?.end_date);
  if (!taskStart && !taskEnd) return false;

  const rangeStart = taskStart || taskEnd;
  const rangeEnd = taskEnd || taskStart;

  if (start && rangeEnd < start) return false;
  if (end && rangeStart > end) return false;

  return true;
};

const extractFilenameFromUrl = (url) => {
  if (!url) return "Attachment";

  try {
    const parsedUrl = new URL(url);
    const filename = parsedUrl.pathname.split("/").filter(Boolean).pop();
    return decodeURIComponent(filename || "Attachment");
  } catch (error) {
    const filename = String(url).split("?")[0].split("/").filter(Boolean).pop();
    return decodeURIComponent(filename || "Attachment");
  }
};

const getFileExtension = (url) => {
  const filename = extractFilenameFromUrl(url);
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const isImageFile = (url) =>
  ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(
    getFileExtension(url),
  );

const getFileTypeLabel = (url) => {
  const extension = getFileExtension(url);
  if (!extension) return "FILE";
  return `${extension.toUpperCase()} FILE`;
};

const renderComment = (comment) => {
  if (!comment) return null;
  // Split the comment into parts, capturing anything that starts with #APPROVAL_
  // We exclude common punctuation from the badge itself for cleaner look
  const parts = comment.split(/(#APPROVAL_[^\s,.:;!?]+)/g);
  return parts.map((part, index) => {
    if (part.startsWith("#APPROVAL_")) {
      return (
        <span
          key={index}
          className="mx-0.5 inline-block rounded bg-purple-100 px-1.5 text-[10px] font-bold uppercase tracking-wider text-purple-700 border border-purple-200 shadow-sm align-middle"
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

const isTextFile = (url) =>
  ["txt", "md", "csv", "log", "json", "xml"].includes(getFileExtension(url));

const getRelativeTime = (dateValue) => {
  if (!dateValue) return "";
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return "";

  const seconds = Math.round((parsedDate.getTime() - Date.now()) / 1000);
  const absSeconds = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  if (absSeconds < 60) return rtf.format(seconds, "second");
  if (absSeconds < 3600) return rtf.format(Math.round(seconds / 60), "minute");
  if (absSeconds < 86400) return rtf.format(Math.round(seconds / 3600), "hour");
  return rtf.format(Math.round(seconds / 86400), "day");
};

const getFirstId = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;

    if (typeof value === "object") {
      const nestedValue =
        value.subtask_id ?? value.task_id ?? value.id ?? value.pk ?? null;
      if (nestedValue !== null && nestedValue !== undefined && nestedValue !== "") {
        return nestedValue;
      }
      continue;
    }

    return value;
  }

  return null;
};

const mapPriority = (priority) => {
  const priorityMap = {
    2: "High",
    1: "Medium",
    0: "Low",
  };
  return priorityMap[priority] || "Unknown";
};

const normalizeSubtask = (item, task = {}) => {
  const id = getFirstId(item?.subtask_id, item?.id);
  const taskId = getFirstId(item?.task_id, item?.task, task?.id);

  return {
    id,
    task_id: taskId,
    task_group: task?.task_group ?? task?.groupId ?? null,
    subtask_name: item?.subtask_name || item?.name || "Untitled Subtask",
    name: item?.subtask_name || item?.name || "Untitled Subtask",
    task_name: item?.task_name || task?.name || "",
    status: item?.is_completed ? "Completed" : item?.status || "Ongoing",
    start_date: item?.start_date || null,
    end_date: item?.end_date || null,
    is_completed: Boolean(item?.is_completed),
    assignee: Array.isArray(item?.Assignees)
      ? item.Assignees
      : Array.isArray(item?.assign_to)
        ? item.assign_to
        : [],
  };
};

const normalizeUpdate = (item) => {
  const approvalStatus = item?.approval_status
    ? String(item.approval_status).toLowerCase()
    : null;
  const taskId = getFirstId(item?.task_id, item?.task, item?.taskId);
  const subtaskId = getFirstId(item?.subtask_id, item?.subtask, item?.subtaskId);
  const taskGroupId = getFirstId(
    item?.group_id,
    item?.task_group_id,
    item?.taskGroupId,
  );
  const subtaskName = item?.subtask_name || "";
  const taskName = item?.task_name || "";

  return {
    id: item?.id,
    type: item?.type,
    taskId,
    subtaskId,
    taskGroupId,
    actorName: getDisplayName(item?.created_by || "User"),
    title: subtaskName || taskName || "Task Update",
    subtaskName,
    taskName,
    groupName: item?.task_group_name || "",
    comment: item?.comment || "",
    siteId: item?.site_id,
    createdAt: item?.created_on || item?.date || null,
    file: item?.file || null,
    isRequestForApproval: Boolean(item?.is_request_for_approval),
    canApprove:
      item?.can_approve === undefined || item?.can_approve === null
        ? true
        : Boolean(item.can_approve),
    approvalStatus,
  };
};

const isPendingApproval = (update) =>
  update.isRequestForApproval && update.approvalStatus === "pending";

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const getUpdateBreadcrumb = (update) => {
  const names =
    update?.type === "subtask_update" || update?.subtaskId || update?.subtaskName
      ? [update?.groupName, update?.taskName, update?.subtaskName || update?.title]
      : [update?.groupName, update?.taskName || update?.title];

  return names.filter((name, index, list) => {
    const normalized = normalizeText(name);
    return normalized && list.findIndex((item) => normalizeText(item) === normalized) === index;
  });
};

const renderBreadcrumb = (update) => {
  const parts = getUpdateBreadcrumb(update);
  if (!parts.length) return null;

  const getLevelStyles = (index) => {
    switch (index) {
      case 0:
        return "bg-purple-50 text-purple-700 border-purple-100";
      case 1:
        return "bg-blue-50 text-blue-700 border-blue-100";
      case 2:
        return "bg-teal-50 text-teal-700 border-teal-100";
      default:
        return "bg-gray-50 text-gray-700 border-gray-100";
    }
  };

  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
      {parts.map((part, index) => (
        <React.Fragment key={`${part}-${index}`}>
          {index > 0 && (
            <svg
              className="text-gray-300 shrink-0"
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          )}
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-tight ${getLevelStyles(
              index,
            )}`}
          >
            {part}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const UpdateAttachmentPreview = ({ file }) => {
  if (!file) return null;

  const filename = extractFilenameFromUrl(file);

  if (isImageFile(file)) {
    return (
      <div className="mt-3 w-24 overflow-hidden rounded-md border border-gray-100 bg-gray-50">
        <img
          src={file}
          alt={filename}
          className="h-24 w-24 object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div className="mt-3 flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-2">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600">
        {getFileExtension(file) === "pdf" ? (
          <FiFileText size={18} />
        ) : (
          <FiPaperclip size={18} />
        )}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-xs font-medium text-gray-800">
          {filename}
        </span>
        <span className="block text-[10px] font-medium text-gray-500">
          {getFileTypeLabel(file)}
        </span>
      </span>
    </div>
  );
};

const ApprovalActionModal = ({
  update,
  comment,
  onCommentChange,
  onClose,
  onDownload,
  onSubmit,
  approveLoading,
  rejectLoading,
  disabled,
}) => {
  if (!update) return null;

  const file = update.file;
  const fileExtension = getFileExtension(file);
  const fileName = extractFilenameFromUrl(file);

  const renderPreview = () => {
    if (!file) {
      return (
        <div className="flex min-h-[50vh] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
          No attachment was included with this update.
        </div>
      );
    }

    if (isImageFile(file)) {
      return (
        <div className="flex min-h-[50vh] items-center justify-center">
          <img
            src={file}
            alt={fileName}
            className="max-h-full max-w-full rounded-lg object-contain"
          />
        </div>
      );
    }

    if (fileExtension === "pdf") {
      return (
        <iframe
          src={file}
          className="min-h-[50vh] w-full rounded-lg border border-gray-200"
          title="PDF Preview"
        />
      );
    }

    if (isTextFile(file)) {
      return (
        <div className="min-h-[50vh]">
          <iframe
            src={file}
            className="h-full min-h-[50vh] w-full rounded-lg border border-gray-200 bg-gray-50"
            title="Text Preview"
          />
        </div>
      );
    }

    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center space-y-4">
        <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
          {fileExtension === "pdf" ? (
            <FiFileText size={40} />
          ) : (
            <FiPaperclip size={40} />
          )}
        </div>
        <div className="text-center">
          <p className="mb-1 text-lg font-medium text-gray-900">{fileName}</p>
          <p className="mb-4 text-sm text-gray-500">
            {getFileTypeLabel(file)} - Preview not available
          </p>
          <button
            type="button"
            onClick={() => onDownload(file, fileName)}
            className="inline-flex items-center rounded-lg bg-purple-800 px-4 py-2 text-white transition-colors hover:bg-purple-700"
          >
            <FiDownload className="mr-2" size={16} />
            Download File
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="approval-action-modal-title"
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h3
                id="approval-action-modal-title"
                className="text-lg font-semibold text-gray-900"
              >
                Take Action
              </h3>
              {file ? (
                <span className="truncate text-sm text-gray-500">{fileName}</span>
              ) : null}
            </div>
            <p className="mt-1 text-sm text-gray-500">{update.title}</p>
          </div>
          <div className="flex items-center gap-2">
            {file ? (
              <button
                type="button"
                onClick={() => onDownload(file, fileName)}
                className="inline-flex items-center rounded-lg bg-purple-800 px-3 py-2 text-sm text-white transition-colors hover:bg-purple-700"
              >
                <FiDownload className="mr-2" size={16} />
                Download
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              disabled={disabled}
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">{renderPreview()}</div>

        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <textarea
            className="w-full resize-none rounded-lg border border-gray-300 p-3 text-sm transition-colors focus:border-transparent focus:ring-2 focus:ring-purple-500"
            rows={3}
            value={comment}
            onChange={(event) => onCommentChange(event.target.value)}
            placeholder="Add a comment (optional)"
            disabled={disabled}
          />
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 bg-white p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={disabled}
            className="rounded-lg border border-gray-300 px-4 py-2 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onSubmit("reject")}
              disabled={disabled}
              className="inline-flex items-center rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-800 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiXCircle className="mr-2" size={16} />
              {rejectLoading ? "Rejecting..." : "Issue detected"}
            </button>
            <button
              type="button"
              onClick={() => onSubmit("approve")}
              disabled={disabled}
              className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 font-medium text-white transition-colors hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiCheckCircle className="mr-2" size={16} />
              {approveLoading ? "Approving..." : "Approve file"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const normalizeTask = (item) => {
  const id = getFirstId(item?.task_id, item?.id);
  const completedSubtasks = item?.subtasks.filter((subtask) => subtask.is_completed).length;
  const totalSubtasks = item?.subtasks.length;

  const task = {
    id,
    task_id: id,
    task_group: item?.task_group_id ?? null,
    groupId: item?.task_group_id ?? null,
    name: item?.task_name || item?.name || "Untitled Task",
    task_name: item?.task_name || item?.name || "Untitled Task",
    groupName: item?.task_group || "",
    status: String(item?.task_status || item?.status || "").toLowerCase(),
    statusLabel: item?.task_status || item?.status || "",
    task_status: item?.task_status || item?.status || "",
    priority: mapPriority(item?.priority),
    startDate: item?.start_date || null,
    progress: totalSubtasks > 0 ? (completedSubtasks / totalSubtasks) * 100 : 0,
    endDate: item?.end_date || null,
    start_date: item?.start_date || null,
    end_date: item?.end_date || null,
    assignees: Array.isArray(item?.Assignees) ? item.Assignees : [],
  };

  task.subtasks = Array.isArray(item?.subtasks)
    ? item.subtasks.map((subtask) => normalizeSubtask(subtask, task))
    : [];

  return task;
};

const getTaskBucket = (task) => {
  if (
    task.status.includes("halted") ||
    task.status.includes("hold") ||
    task.status.includes("paused")
  ) {
    return "halted";
  }
  if (task.status.includes("not started")) return "notStarted";
  if (task.status.includes("complete")) return null;
  if (task.status.includes("ongoing")) {
    if (!task.endDate) return "ongoing";

    const end = new Date(task.endDate);
    if (Number.isNaN(end.getTime())) return "ongoing";

    // Compare only date portions to avoid timezone/time-of-day misclassification.
    const today = new Date();
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    return endDateOnly < todayStart ? "overdue" : "ongoing";
  }
  return null;
};

const TaskDashboardView = ({ onOpenTask, onOpenSubtask }) => {
  const { addAlert } = useAlert();
  const siteId = Number(localStorage.getItem("site"));
  const [updates, setUpdates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [updatesError, setUpdatesError] = useState("");
  const [tasksError, setTasksError] = useState("");
  const [approvalAction, setApprovalAction] = useState(null);
  const [selectedApprovalUpdate, setSelectedApprovalUpdate] = useState(null);
  const [approvalComment, setApprovalComment] = useState("");
  const [taskDateRange, setTaskDateRange] = useState({ start: "", end: "" });

  const loadUpdates = useCallback(async () => {
    setUpdatesLoading(true);
    setUpdatesError("");

    try {
      const allUpdates = await taskApi.getAllUpdatesByOrg(undefined, siteId);
      setUpdates(
        (Array.isArray(allUpdates) ? allUpdates : []).map(normalizeUpdate),
      );
    } catch (err) {
      setUpdatesError(err?.message || "Failed to load updates.");
      setUpdates([]);
    } finally {
      setUpdatesLoading(false);
    }
  }, [siteId]);

  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    setTasksError("");

    try {
      const tasksBySite = await taskApi.getTasksBySite(siteId);
      setTasks((Array.isArray(tasksBySite) ? tasksBySite : []).map(normalizeTask));
    } catch (err) {
      setTasksError(err?.message || "Failed to load tasks.");
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadUpdates();
    loadTasks();
  }, [loadTasks, loadUpdates]);

  const sortedUpdates = useMemo(
    () =>
      [...updates].sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
      ),
    [updates],
  );

  const recentUpdates = sortedUpdates;
  const pendingApprovals = useMemo(
    () => sortedUpdates.filter(isPendingApproval),
    [sortedUpdates],
  );

  const findTaskForUpdate = (update) => {
    const updateTaskId = update?.taskId ? String(update.taskId) : "";
    if (updateTaskId) {
      const matchedTask = tasks.find((task) => String(task.id) === updateTaskId);
      if (matchedTask) return matchedTask;
    }

    const taskName = normalizeText(update?.taskName || update?.title);
    if (!taskName) return null;

    return (
      tasks.find(
        (task) =>
          normalizeText(task.name) === taskName ||
          normalizeText(task.task_name) === taskName,
      ) || null
    );
  };

  const findSubtaskForUpdate = (update) => {
    const updateSubtaskId = update?.subtaskId ? String(update.subtaskId) : "";
    if (updateSubtaskId) {
      for (const task of tasks) {
        const matchedSubtask = task.subtasks.find(
          (subtask) => String(subtask.id) === updateSubtaskId,
        );
        if (matchedSubtask) return matchedSubtask;
      }
    }

    const subtaskName = normalizeText(update?.title);
    const parentTaskName = normalizeText(update?.taskName);
    if (!subtaskName || subtaskName === parentTaskName) return null;

    for (const task of tasks) {
      if (
        parentTaskName &&
        normalizeText(task.name) !== parentTaskName &&
        normalizeText(task.task_name) !== parentTaskName
      ) {
        continue;
      }

      const matchedSubtask = task.subtasks.find(
        (subtask) =>
          normalizeText(subtask.name) === subtaskName ||
          normalizeText(subtask.subtask_name) === subtaskName,
      );
      if (matchedSubtask) return matchedSubtask;
    }

    return null;
  };

  const openTaskDetails = (task) => {
    if (typeof onOpenTask !== "function" || !task?.id) return;
    onOpenTask(task);
  };

  const openSubtaskDetails = (subtask) => {
    if (typeof onOpenSubtask !== "function" || !subtask?.id || !subtask?.task_id) {
      return;
    }
    onOpenSubtask(subtask);
  };

  const canOpenUpdate = (update) => {
    if (update?.subtaskId && update?.taskId && typeof onOpenSubtask === "function") {
      return true;
    }
    if (findSubtaskForUpdate(update) && typeof onOpenSubtask === "function") {
      return true;
    }
    return Boolean(
      (update?.taskId || findTaskForUpdate(update)) &&
      typeof onOpenTask === "function",
    );
  };

  const openUpdateDetails = (update) => {
    const matchedSubtask = findSubtaskForUpdate(update);
    if (matchedSubtask) {
      openSubtaskDetails(matchedSubtask);
      return;
    }

    if (update?.subtaskId && update?.taskId) {
      openSubtaskDetails({
        id: update.subtaskId,
        task_id: update.taskId,
        task_group: update.taskGroupId,
        subtask_name: update.title,
        name: update.title,
        task_name: update.taskName,
      });
      return;
    }

    const matchedTask = findTaskForUpdate(update);
    if (matchedTask) {
      openTaskDetails(matchedTask);
      return;
    }

    if (update?.taskId) {
      openTaskDetails({
        id: update.taskId,
        task_id: update.taskId,
        task_group: update.taskGroupId,
        name: update.taskName || update.title,
        task_name: update.taskName || update.title,
      });
    }
  };

  const canApproveUpdate = (update) =>
    isPendingApproval(update) &&
    update?.type === "subtask_update" &&
    update?.canApprove !== false;

  const closeApprovalModal = useCallback(() => {
    if (approvalAction?.updateId === selectedApprovalUpdate?.id) return;
    setSelectedApprovalUpdate(null);
    setApprovalComment("");
  }, [approvalAction, selectedApprovalUpdate]);

  const handleDownload = useCallback((url, filename) => {
    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.download = filename || extractFilenameFromUrl(url) || "download";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const openApprovalModal = (update, event) => {
    event.stopPropagation();
    if (!update?.id || !canApproveUpdate(update)) return;
    setSelectedApprovalUpdate(update);
    setApprovalComment("");
  };

  const handleApprovalAction = async (update, action) => {
    if (!update?.id || !canApproveUpdate(update)) return;

    setApprovalAction({ updateId: update.id, action });
    try {
      const comment = `Regarding #APPROVAL_${update.id}: ${approvalComment.trim()}`;
      const response = await taskApi.approveRejectSubtaskUpdate(
        update.id,
        action,
        comment,
      );

      if (response?.Status === false || response?.status === false) {
        throw new Error(response?.message || response?.detail || "Action failed.");
      }

      addAlert(
        action === "approve"
          ? "Update approved successfully!"
          : "Update rejected successfully!",
        "success",
      );
      setSelectedApprovalUpdate(null);
      setApprovalComment("");
      await loadUpdates();
    } catch (error) {
      console.error("Approval action failed:", error);
      addAlert(
        action === "approve"
          ? "Could not approve update. Please try again."
          : "Could not reject update. Please try again.",
        "error",
      );
    } finally {
      setApprovalAction(null);
    }
  };

  useEffect(() => {
    if (!selectedApprovalUpdate) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeApprovalModal();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [closeApprovalModal, selectedApprovalUpdate]);

  const handlePendingCardKeyDown = (event, update) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    if (canOpenUpdate(update)) {
      openUpdateDetails(update);
    }
  };

  const hasTaskDateFilter = Boolean(taskDateRange.start || taskDateRange.end);

  const taskBuckets = useMemo(() => {
    const initial = {
      overdue: [],
      ongoing: [],
      notStarted: [],
      halted: [],
    };

    for (const task of tasks) {
      if (!taskMatchesDateRange(task, taskDateRange.start, taskDateRange.end)) {
        continue;
      }

      const bucket = getTaskBucket(task);
      if (bucket && initial[bucket]) {
        initial[bucket].push(task);
      }
    }

    return initial;
  }, [taskDateRange.end, taskDateRange.start, tasks]);

  const filteredTaskCount = useMemo(
    () =>
      Object.values(taskBuckets).reduce(
        (total, bucketTasks) => total + bucketTasks.length,
        0,
      ),
    [taskBuckets],
  );

  return (
    <div className="p-6 min-h-screen">
      {/* <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A0033]">Task Dashboard</h1>
          <p className="text-gray-500 text-sm">Overview of your project activity</p>
        </div>
      </div> */}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        <div className="lg:col-span-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-purple-100 text-purple-700 flex items-center justify-center">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              Recent Updates
            </h2>
          </div>

          <div className="max-h-[460px] space-y-4 overflow-y-auto pr-2" style={{
            scrollbarWidth: "none"
          }}>
            {updatesLoading ? (
              <p className="text-sm text-gray-500">Loading recent updates...</p>
            ) : updatesError ? (
              <p className="text-sm text-red-500">{updatesError}</p>
            ) : recentUpdates.length === 0 ? (
              <p className="text-sm text-gray-500">No updates found for this site.</p>
            ) : (
              recentUpdates.map((update) => (
                <button
                  type="button"
                  key={update.id || `${update.title}-${update.createdAt}`}
                  className="flex w-full gap-3 border-b rounded-lg p-2 text-left transition hover:bg-gray-50 disabled:hover:bg-transparent disabled:cursor-default"
                  disabled={!canOpenUpdate(update)}
                  onClick={() => openUpdateDetails(update)}
                >
                  <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-medium shrink-0">
                    {getInitials(update.actorName)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800 leading-relaxed">
                      <span className="font-medium">{update.actorName}</span>{" "}
                      {update.isRequestForApproval
                        ? "requested approval on"
                        : "added an update on"}{" "}
                      <span className="font-medium text-[#1A0033]">{update.title}</span>
                      {isPendingApproval(update) && (
                        <span className="ml-2 inline-block text-[10px] px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full border border-orange-100 whitespace-nowrap align-middle">
                          Pending Approval
                        </span>
                      )}
                      {
                        update.isRequestForApproval && update.approvalStatus === "approved" && (
                          <span className="ml-2 inline-block text-[10px] px-2 py-0.5 bg-green-50 text-green-600 rounded-full border border-green-100 whitespace-nowrap align-middle">Approved</span>
                        )
                      }
                      {
                        update.isRequestForApproval && update.approvalStatus === "rejected" && (
                          <span className="ml-2 inline-block text-[10px] px-2 py-0.5 bg-red-50 text-red-600 rounded-full border border-red-100 whitespace-nowrap align-middle">Rejected</span>
                        )
                      }
                    </p>
                    {update.comment ? (
                      <p className="text-sm text-gray-500 mt-1">
                        {renderComment(update.comment)}
                      </p>
                    ) : null}
                    <UpdateAttachmentPreview file={update.file} />
                    {renderBreadcrumb(update)}
                    <div className="flex items-center justify-end gap-3 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <FiClock /> {getRelativeTime(update.createdAt)}
                      </span>
                      {update.file ? (
                        <span className="flex items-center gap-1">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                          </svg>
                          1 file
                        </span>
                      ) : null}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="lg:col-span-2 bg-[#FEFBF6] rounded-xl p-4 shadow-sm border border-[#FFEFE5]">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-orange-100 text-orange-500 flex items-center justify-center">
                <FiCheckCircle size={14} />
              </div>
              Pending Approvals
            </h2>
            <span className="bg-orange-100 text-orange-600 text-xs px-2 py-0.5 rounded-full">
              {pendingApprovals.length}
            </span>
          </div>

          <div className="max-h-[460px] space-y-4 overflow-y-auto pr-2">
            {updatesLoading ? (
              <p className="text-sm text-gray-500">Loading pending approvals...</p>
            ) : updatesError ? (
              <p className="text-sm text-red-500">Could not load pending approvals.</p>
            ) : pendingApprovals.length === 0 ? (
              <p className="text-sm text-gray-500">No pending approvals for this site.</p>
            ) : (
              pendingApprovals.map((update) => {
                const takeActionDisabled =
                  !canApproveUpdate(update) || approvalAction?.updateId === update.id;
                const approveLoading =
                  approvalAction?.updateId === update.id &&
                  approvalAction?.action === "approve";
                const rejectLoading =
                  approvalAction?.updateId === update.id &&
                  approvalAction?.action === "reject";

                return (
                  <div
                    key={update.id || `pending-${update.title}-${update.createdAt}`}
                    role={canOpenUpdate(update) ? "button" : undefined}
                    tabIndex={canOpenUpdate(update) ? 0 : undefined}
                    className={`bg-white border border-gray-100 rounded-xl p-3 shadow-sm text-left w-full transition ${canOpenUpdate(update)
                        ? "cursor-pointer hover:border-orange-200 hover:shadow-md"
                        : ""
                      }`}
                    onClick={() => {
                      if (canOpenUpdate(update)) openUpdateDetails(update);
                    }}
                    onKeyDown={(event) => handlePendingCardKeyDown(event, update)}
                  >
                    <h3 className="font-medium text-sm text-gray-800">{update.title}</h3>
                    {renderBreadcrumb(update)}

                    <UpdateAttachmentPreview file={update.file} />
                    {update.comment ? (
                      <div className="bg-gray-50 px-3 py-2 mt-2 rounded-md text-xs text-gray-600 mb-2 flex gap-2 items-start">
                        <svg
                          className="shrink-0 mt-0.5"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        <span>{renderComment(update.comment)}</span>
                      </div>
                    ) : null}

                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[10px] font-medium">
                        {getInitials(update.actorName)}
                      </div>
                      <span className="text-xs text-gray-400">
                        {getRelativeTime(update.createdAt)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <button
                        type="button"
                        disabled={takeActionDisabled}
                        title={
                          canApproveUpdate(update)
                            ? "Take action"
                            : "Only assigned approvers can take action on this update"
                        }
                        onClick={(event) => openApprovalModal(update, event)}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-[#3B0069] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#2a004b] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <FiCheckCircle size={14} />
                        {approveLoading || rejectLoading ? "Processing..." : "Take action"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {selectedApprovalUpdate ? (
        <ApprovalActionModal
          update={selectedApprovalUpdate}
          comment={approvalComment}
          onCommentChange={setApprovalComment}
          onClose={closeApprovalModal}
          onDownload={handleDownload}
          onSubmit={(action) => handleApprovalAction(selectedApprovalUpdate, action)}
          approveLoading={
            approvalAction?.updateId === selectedApprovalUpdate.id &&
            approvalAction?.action === "approve"
          }
          rejectLoading={
            approvalAction?.updateId === selectedApprovalUpdate.id &&
            approvalAction?.action === "reject"
          }
          disabled={approvalAction?.updateId === selectedApprovalUpdate.id}
        />
      ) : null}

      <div>
        <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h2 className="font-semibold text-gray-800">Task Overview by Status</h2>
            {!tasksLoading ? (
              <p className="mt-1 text-xs text-gray-500">
                {hasTaskDateFilter
                  ? `Showing ${filteredTaskCount} of ${tasks.length} tasks in the selected date range.`
                  : `Showing all ${tasks.length} scheduled tasks.`}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                From
              </label>
              <input
                type="date"
                value={taskDateRange.start}
                max={taskDateRange.end || undefined}
                onChange={(event) =>
                  setTaskDateRange((current) => ({
                    ...current,
                    start: event.target.value,
                  }))
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 sm:w-40"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                To
              </label>
              <input
                type="date"
                value={taskDateRange.end}
                min={taskDateRange.start || undefined}
                onChange={(event) =>
                  setTaskDateRange((current) => ({
                    ...current,
                    end: event.target.value,
                  }))
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 sm:w-40"
              />
            </div>

            <button
              type="button"
              onClick={() => setTaskDateRange({ start: "", end: "" })}
              disabled={!hasTaskDateFilter}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiX size={14} />
              Clear
            </button>
          </div>
        </div>
        {tasksError ? (
          <p className="text-sm text-red-500">{tasksError}</p>
        ) : null}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {STATUS_CARD_CONFIG.map((statusCard) => {
            const entries = taskBuckets[statusCard.key] || [];
            return (
              <div
                key={statusCard.key}
                className={`${statusCard.cardClass} rounded-xl px-3 py-3 flex flex-col`}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium text-sm text-gray-800 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${statusCard.dotClass}`}></span>
                    {statusCard.title}
                  </h3>
                  <span
                    className={`text-xs border bg-white rounded-full w-5 h-5 flex items-center justify-center ${statusCard.countClass}`}
                  >
                    {tasksLoading ? "-" : entries.length}
                  </span>
                </div>

                {tasksLoading ? (
                  <p className="text-sm text-gray-500">Loading tasks...</p>
                ) : entries.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                    {hasTaskDateFilter ? "No tasks in range" : "No tasks"}
                  </div>
                ) : (
                  <div className="space-y-3 h-[50vh] overflow-y-scroll">
                    {entries.map((task) => (
                      <button
                        type="button"
                        key={task.id || task.name}
                        className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 text-left w-full transition hover:border-purple-200 hover:shadow-md disabled:hover:border-gray-100 disabled:hover:shadow-sm disabled:cursor-default"
                        disabled={typeof onOpenTask !== "function" || !task.id}
                        onClick={() => openTaskDetails(task)}
                      >
                        <div className="flex justify-between items-start mb-1 gap-2">
                          <h4 className="font-medium text-sm text-gray-800 truncate">
                            {task.name}
                          </h4>
                          <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full shrink-0">
                            {task.priority || "Low"}
                          </span>
                        </div>
                        <p className="text-[10px] text-gray-400 mb-3 truncate">
                          {task.groupName || "Task Group"}
                        </p>
                        <div className="flex justify-between items-center">
                          <div className="flex -space-x-1">
                            {task.assignees.slice(0, 3).map((assignee) => (
                              <div
                                key={assignee.task_member_id || assignee.member_id}
                                className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[8px] font-medium border border-white"
                              >
                                {getInitials(
                                  `${assignee.first_name || ""} ${assignee.last_name || ""}`,
                                )}
                              </div>
                            ))}
                            {task.assignees.length > 3 ? (
                              <div className="w-5 h-5 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-[8px] font-medium border border-white">
                                +{task.assignees.length - 3}
                              </div>
                            ) : null}
                          </div>
                          <span className="text-[10px] text-gray-500">
                            {formatDateShort(task.startDate)} → {formatDateShort(task.endDate)}
                          </span>
                        </div>
                        {/* progress bar */}
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div className="bg-sm-primary h-1.5 rounded-full" style={{ width: `${task.progress}%` }}></div>
                          </div>
                          <span className="text-[10px] text-gray-500">{task.progress.toFixed(2)}%</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TaskDashboardView;
